import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";

/** Daily agent-budget pill. Reads /api/dashboard/stats (already polled by the
 *  shell). Shows "—" when the daily cap is missing so the chip is always
 *  visible on both desktop and mobile.
 */
export function BudgetChip({ className = "" }) {
  const { data: stats } = useQuery({
    queryKey: ["dashboard-stats-nav"],
    queryFn: async () => (await api.get("/dashboard/stats")).data,
    refetchInterval: 20000,
    retry: 0,
  });
  const { data: agents } = useQuery({
    queryKey: ["agents-registry"],
    queryFn: async () => (await api.get("/settings/agents")).data,
    staleTime: 30000,
    retry: 0,
  });

  const today = stats?.costs?.today || {};
  // Prefer total_usd (sum of USD + emergent) so the chip reflects wall spend.
  const used = Number(today.total_usd ?? today.usd ?? 0);
  const cap = Number(today.budget_usd ?? 0);
  const hasData = cap > 0;
  const pct = hasData ? Math.min(100, (used / cap) * 100) : 0;
  const color = pct >= 100 ? "#ef4444" : pct >= 80 ? "#f59e0b" : "var(--cobalt)";

  const label = hasData
    ? <><span className="text-[color:var(--fg)]">${used.toFixed(2)}</span><span> / ${cap.toFixed(2)} today</span></>
    : <span className="text-[color:var(--muted)]">— / — today</span>;

  const agentRows = (agents?.agents || [])
    .filter((a) => (a.monthly_spent_usd || 0) > 0)
    .sort((a, b) => (b.monthly_spent_usd || 0) - (a.monthly_spent_usd || 0))
    .slice(0, 10);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid="budget-chip"
          className={`flex items-center gap-2 px-3 py-1.5 rounded-[6px] border border-[color:var(--line)] bg-[color:var(--panel)] hover:border-[color:var(--cobalt)] transition-colors ${className}`}
        >
          <span className="font-mono text-[11px]">{label}</span>
          <span className="relative w-16 md:w-20 h-1 rounded-full bg-[color:var(--line)] overflow-hidden">
            <span className="absolute inset-y-0 left-0"
                  style={{ width: `${pct}%`, background: color }} />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end" data-testid="budget-popover">
        <p className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-[color:var(--muted)] mb-2">
          Today's agent spend
        </p>
        {agentRows.length === 0 ? (
          <p className="text-xs text-[color:var(--muted)]" data-testid="budget-popover-empty">
            Dökümü yok · No breakdown
          </p>
        ) : (
          <ul className="space-y-1.5" data-testid="budget-popover-list">
            {agentRows.map((a) => (
              <li key={a.id} className="flex items-center justify-between text-xs">
                <span className="truncate pr-2">{a.name}</span>
                <span className="font-mono tabular-nums text-[color:var(--muted)]">
                  ${(a.monthly_spent_usd || 0).toFixed(2)}<span className="text-[9px]"> MTD</span>
                </span>
              </li>
            ))}
          </ul>
        )}
        <p className="font-mono text-[10px] text-[color:var(--muted)] mt-3">
          {hasData
            ? <>Daily cap: ${cap.toFixed(2)} · {pct.toFixed(0)}% used</>
            : <>Daily cap not set in Settings → Billing.</>}
        </p>
      </PopoverContent>
    </Popover>
  );
}
